import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { LiveDot } from '@/components/generals/live-dot'
import { GuideLines } from '@/components/generals/dark-band'
import { footerColumns } from '@/constants/navigation'

/** Dark footer that bookends every page with the same charcoal as the dark bands. */
export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer
            data-header-theme='dark'
            className='relative overflow-hidden rounded-t-[32px] bg-charcoal text-white sm:rounded-t-[48px] lg:rounded-t-[64px]'
        >
            <GuideLines />

            <div aria-hidden='true' className='pointer-events-none absolute -top-40 right-[-10%] h-80 w-[50%] rounded-full bg-aurora opacity-[0.07] blur-3xl' />

            <div className='shell relative pb-10 pt-20 sm:pt-24'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <Link
                            href='/'
                            aria-label='Quayside home'
                            className='inline-flex rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
                        >
                            <Logo />
                        </Link>

                        <p className='headline mt-10 max-w-md text-[clamp(2.2rem,4.4vw,3.4rem)]'>
                            <span className='block text-white'>
                                Every port connected.
                            </span>

                            <span className='text-aurora'>
                                Every box accounted for.
                            </span>
                        </p>

                        <Link
                            href='/contact'
                            className='group/cta mt-10 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
                        >
                            Book a walkthrough
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover/cta:translate-x-0.5' />
                        </Link>
                    </div>

                    <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7 lg:pl-10'>
                        {footerColumns.map(column => (
                            <div key={column.title}>
                                <h2 className='eyebrow text-white/40'>
                                    {column.title}
                                </h2>

                                <ul className='mt-5 space-y-3'>
                                    {column.links.map(link => (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                className='rounded-sm text-[14px] text-mist transition-colors duration-200 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </div>

                <div className='mt-20 flex flex-col gap-4 border-t border-white/10 pt-6 text-[13px] text-mist sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © 2026 Quayside Ltd. All rights reserved.
                    </p>

                    <Link
                        href='/contact#system-status'
                        className='inline-flex items-center gap-2.5 rounded-sm transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
                    >
                        <LiveDot />
                        All systems running
                    </Link>
                </div>
            </div>
        </footer>
    )
}
